/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import './TabViewStyles.scss';
/**
 * TabView component for organizing content into tabs
 * Supports both horizontal and vertical layouts
 *
 * @example
 * ```tsx
 * <TabView
 *   tabs={[
 *     { id: 'tab1', label: 'First Tab', content: <div>Content 1</div> },
 *     { id: 'tab2', label: 'Second Tab', content: <div>Content 2</div> }
 *   ]}
 *   defaultTabId="tab1"
 * />
 * ```
 */
export function TabView({ tabs, defaultTabId, activeTabId: externalActiveTabId, onChange, vertical = false, className, children }) {
    const [internalActiveTabId, setInternalActiveTabId] = useState(() => {
        if (externalActiveTabId)
            return externalActiveTabId;
        if (defaultTabId)
            return defaultTabId;
        return tabs.length > 0 ? tabs[0].id : '';
    });
    const isControlled = externalActiveTabId !== undefined;
    const activeTabId = isControlled ? externalActiveTabId : internalActiveTabId;
    useEffect(() => {
        if (externalActiveTabId) {
            setInternalActiveTabId(externalActiveTabId);
        }
    }, [externalActiveTabId]);
    // Validate active tab when tabs array changes (uncontrolled mode only)
    useEffect(() => {
        // Only validate in uncontrolled mode
        if (isControlled) {
            return;
        }
        // Check if current active tab still exists in tabs array
        const tabExists = tabs.some((tab) => tab.id === internalActiveTabId);
        // If active tab no longer exists, reset to a sensible fallback
        if (!tabExists) {
            // Check if defaultTabId exists in the tabs array
            const defaultTabExists = defaultTabId && tabs.some((t) => t.id === defaultTabId);
            let fallbackTabId = '';
            if (defaultTabExists) {
                fallbackTabId = defaultTabId;
            }
            else if (tabs.length > 0) {
                fallbackTabId = tabs[0].id;
            }
            setInternalActiveTabId(fallbackTabId);
        }
    }, [tabs, internalActiveTabId, isControlled, defaultTabId]);
    const handleTabClick = (tabId, disabled, e) => {
        if (disabled) {
            e === null || e === void 0 ? void 0 : e.preventDefault();
            return;
        }
        e === null || e === void 0 ? void 0 : e.preventDefault();
        if (!isControlled) {
            setInternalActiveTabId(tabId);
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(tabId);
    };
    if (!tabs || tabs.length === 0) {
        return null;
    }
    const activeTab = tabs.find((tab) => tab.id === activeTabId);
    const renderTabContent = () => {
        if (children) {
            return children;
        }
        if (activeTab === null || activeTab === void 0 ? void 0 : activeTab.content) {
            return activeTab.content;
        }
        return null;
    };
    if (vertical) {
        return (React.createElement("div", { className: classNames('qmb-ui-tabs', 'qmb-ui-tabs--vertical', className) },
            React.createElement("div", { className: "qmb-ui-tabs__list", role: "tablist", "aria-orientation": "vertical" }, tabs.map((tab) => (React.createElement("button", { type: "button", key: tab.id, id: `tab-${tab.id}`, className: classNames('qmb-ui-tabs__option', {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'qmb-ui-tabs__option--current': tab.id === activeTabId,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'qmb-ui-tabs__option--disabled': tab.disabled
                }), role: "tab", "aria-selected": tab.id === activeTabId, "aria-controls": `tabpanel-${tab.id}`, "aria-disabled": tab.disabled, disabled: tab.disabled, title: tab.disabled ? tab.disabledTooltip : undefined, onClick: (e) => handleTabClick(tab.id, tab.disabled, e) },
                tab.label,
                tab.count !== undefined && React.createElement("span", { className: "counter" }, tab.count))))),
            React.createElement("div", { className: "qmb-ui-tabs__content" }, tabs.map((tab) => (React.createElement("div", { key: tab.id, id: `tabpanel-${tab.id}`, role: "tabpanel", "aria-labelledby": `tab-${tab.id}`, className: classNames('qmb-ui-tabs__panel', {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'qmb-ui-tabs__panel--active': tab.id === activeTabId
                }), hidden: tab.id !== activeTabId }, tab.id === activeTabId && renderTabContent()))))));
    }
    return (React.createElement("div", { className: classNames('qmb-ui-tabs', className) },
        React.createElement("ul", { className: "qmb-ui-tabs__list", role: "tablist" }, tabs.map((tab) => (React.createElement("li", { key: tab.id, className: classNames({
                active: tab.id === activeTabId
            }) },
            React.createElement("a", { href: `#${tab.id}`, id: `tab-${tab.id}`, className: classNames('qmb-ui-tabs__option', {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'qmb-ui-tabs__option--current': tab.id === activeTabId,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'qmb-ui-tabs__option--disabled': tab.disabled
                }), role: "tab", "aria-selected": tab.id === activeTabId, "aria-controls": `tabpanel-${tab.id}`, "aria-disabled": tab.disabled, title: tab.disabled ? tab.disabledTooltip : undefined, onClick: (e) => handleTabClick(tab.id, tab.disabled, e) },
                tab.label,
                tab.count !== undefined && React.createElement("span", { className: "counter" }, tab.count)))))),
        React.createElement("div", { className: "qmb-ui-tabs__content" }, tabs.map((tab) => (React.createElement("article", { key: tab.id, id: `tabpanel-${tab.id}`, className: classNames('qmb-ui-tabs__panel', {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'qmb-ui-tabs__panel--active': tab.id === activeTabId
            }), role: "tabpanel", "aria-labelledby": `tab-${tab.id}`, hidden: tab.id !== activeTabId }, tab.id === activeTabId && renderTabContent()))))));
}
